/**
 * Textarea コントロール。Perimeter Text の本文入力に使う。
 *
 * 設計書 spec §6（`text`）/ §7（Perimeter Text セクション）/ FR-33 / FR-38。
 * wireframe `wireframes/v0.10/wireframe.html` L52–53 の textarea スタイルを
 * app.css 側で再現する。
 *
 * IME（Input Method Editor）対応:
 * 日本語変換中は `compositionstart` 以降 `compositionend` までの間、
 * store への書き戻しを止める。変換途中のローマ字混じりテキストが
 * state → render → text layout まで伝搬して毎フレーム再計算されるのを
 * 避け、さらに `store.subscribe` が textarea.value を上書きして変換が
 * 途切れるのも防ぐ（textarea 上では latest raw string を保持する）。
 */

import type { AppState, Store } from '../../state';

/**
 * `mountTextarea` がバインドできる `AppState` の string フィールド。
 * 現状は `text` のみ。Phase D 以降で presets 名など追加される可能性に備え
 * 型で拡張できる形にしている。
 */
type TextareaField = 'text';

/** `mountTextarea` のオプション。 */
export interface TextareaOptions {
  /** `AppState` のどの文字列フィールドに書き込むか。 */
  field: TextareaField;
  /** `placeholder` 属性。省略時は未設定。 */
  placeholder?: string;
}

/**
 * Textarea を root にマウントして、指定 field と双方向バインドする。
 *
 * @param root - セクションのコンテナ
 * @param store - グローバルストア
 * @param options - field / placeholder
 * @returns teardown 関数。subscribe 解除 + DOM 撤去。
 */
export function mountTextarea(
  root: HTMLElement,
  store: Store<AppState>,
  options: TextareaOptions,
): () => void {
  const textarea = document.createElement('textarea');
  textarea.value = store.get()[options.field];
  if (options.placeholder !== undefined) {
    textarea.placeholder = options.placeholder;
  }

  // IME 変換中フラグ。compositionstart..end 間は store.set を抑止する。
  let composing = false;

  const onInput = (): void => {
    if (composing) return;
    store.set({ [options.field]: textarea.value } as Partial<AppState>);
  };
  const onCompositionStart = (): void => {
    composing = true;
  };
  const onCompositionEnd = (): void => {
    composing = false;
    // 変換確定時点の value を即反映する（input イベントは compositionend の
    // 直前に飛ぶブラウザと直後に飛ぶブラウザがあるため明示的に書き戻す）。
    store.set({ [options.field]: textarea.value } as Partial<AppState>);
  };

  textarea.addEventListener('input', onInput);
  textarea.addEventListener('compositionstart', onCompositionStart);
  textarea.addEventListener('compositionend', onCompositionEnd);

  // 外部（preset 等）からの store 変更を textarea に反映する。
  // ただし IME 変換中は触らない（上書きで変換が途切れるため）。また
  // 自分が発火した set による再入を避けるため、文字列一致チェックを入れる。
  const unsubscribe = store.subscribe((state, prev) => {
    if (composing) return;
    if (state[options.field] !== prev[options.field]) {
      const next = state[options.field];
      if (textarea.value !== next) textarea.value = next;
    }
  });

  root.appendChild(textarea);

  return (): void => {
    unsubscribe();
    textarea.removeEventListener('input', onInput);
    textarea.removeEventListener('compositionstart', onCompositionStart);
    textarea.removeEventListener('compositionend', onCompositionEnd);
    textarea.remove();
  };
}
